import { useState } from 'react';

import { Alert, Box, Button, Chip, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { SALE_ORDER_STATUS } from '@bandido/shared/constants';
import { HotelFareCard } from '@frontend/features/hotel/components/Fares';

import { useCart } from '../CartContext';
import { SaleOrderItemResponseDto } from '@bandido/shared/dtos';
import { canUseOrdinaryCartDelete, isRuteroCartItemType } from './helper';
import {
  formatRuteroCartCommercialDisplay,
  getRuteroCartRowPresentation,
  RUTERO_CART_BOOKING_STATE_COLORS,
} from '../../ruteroCartBookingAuthority.helpers';
import type { RuteroCartTravelBookingAuthorityEntry } from '../../ruteroCartBookingAuthority';
import {
  bookingStateColor,
  isOnRequest,
} from '@frontend/features/travel-services/bookingState';
import { ActiveOperationChip } from '@frontend/features/travel-services/components/ActiveOperationStatus';
import { BookingDetailModal } from '@frontend/features/travel-services/components/BookingDetailModal';

interface Props {
  service: SaleOrderItemResponseDto
  ruteroAuthority?: RuteroCartTravelBookingAuthorityEntry;
}

export const CartItem = ({ service, ruteroAuthority }: Props) => {

  const { state, removeItem } = useCart();
  const { t } = useTranslation();
  const theme = useTheme();
  const [detailOpen, setDetailOpen] = useState(false);
  const [quotation] = service?.quotations || [];
  const canRemoveFromCart =
    state?.status !== SALE_ORDER_STATUS.Sold &&
    canUseOrdinaryCartDelete(service.type);

  // Item de SERVICIOS (vertical de Rutero): el lifecycle vive en la reserva
  // vinculada, no en el sale_order_item genérico.
  if (isRuteroCartItemType(service.type)) {
    const hasUsableItemId =
      typeof service.id === 'string' && service.id.trim().length > 0;
    const presentation = getRuteroCartRowPresentation(ruteroAuthority, {
      hasUsableItemId,
    });
    const price = formatRuteroCartCommercialDisplay(
      service.price,
      presentation.kind === 'booking' ? presentation.booking.currency : undefined,
    );

    return (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        p={2}
        border={`1px solid ${theme.palette.divider}`}
        borderRadius={2}
      >
        <Box display="flex" flexDirection="column" gap={1.25} minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2">
              {t(`rutero.verticals.${service.type}`)}
            </Typography>
            {presentation.kind === 'booking' && (
              <>
                <Chip
                  size="small"
                  color={bookingStateColor(
                    presentation.booking.state,
                    presentation.booking.providerStatus,
                    RUTERO_CART_BOOKING_STATE_COLORS,
                  )}
                  label={t(presentation.bookingStateLabelKey)}
                />
                {presentation.showUnknownProviderWarning && (
                  <Chip
                    size="small"
                    color="warning"
                    label={t('travelServices.booking.pendingChip')}
                  />
                )}
                <ActiveOperationChip booking={presentation.booking} />
                {presentation.booking.providerStatus &&
                  !isOnRequest(presentation.booking.providerStatus) && (
                    <Chip
                      variant="outlined"
                      size="small"
                      label={presentation.booking.providerStatus}
                    />
                  )}
              </>
            )}
          </Stack>

          {presentation.kind === 'loading' && (
            <Alert severity="info">{t(presentation.messageKey)}</Alert>
          )}
          {presentation.kind === 'unavailable' && (
            <Alert severity="warning">{t(presentation.messageKey)}</Alert>
          )}
          {presentation.kind === 'booking' && (
            <>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {presentation.booking.provider && (
                  <Typography variant="body2" color="text.secondary">
                    {t('rutero.connectors.provider')}: {presentation.booking.provider}
                  </Typography>
                )}
                {presentation.booking.providerReference && (
                  <Typography variant="body2" color="text.secondary">
                    Ref: {presentation.booking.providerReference}
                  </Typography>
                )}
              </Stack>
              {presentation.showUnknownProviderWarning && (
                <Alert severity="warning">
                  {t('travelServices.booking.pendingVerification')}
                </Alert>
              )}
              {presentation.showHoldExpiry && (
                <Alert severity="warning">
                  {t('travelServices.detail.holdExpires', {
                    date: new Date(
                      presentation.booking.expiresAt as string,
                    ).toLocaleString(),
                  })}
                </Alert>
              )}
            </>
          )}
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
          <Typography variant="subtitle1">{price}</Typography>
          {presentation.kind === 'booking' && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDetailOpen(true)}
            >
              {t('travelServices.detail.operate')}
            </Button>
          )}
        </Box>
        {presentation.kind === 'booking' && detailOpen && (
          <BookingDetailModal
            booking={presentation.booking}
            onClose={() => setDetailOpen(false)}
          />
        )}
      </Box>
    );
  }

  if (service.type === 'hotel' && service.metadata) {
    
    return (
      <Box
        display="flex"
        gap={1}
        justifyContent="space-between"
        position="relative"
      >
        <HotelFareCard
          fare={{
            id: service.metadata.fareId,
            propertyName: service.metadata.propertyName,
            optionRefId: service.metadata.optionRefId,
            accessCode: service.metadata.accessCode,
            checkinDate: service.metadata.checkinDate,
            checkoutDate: service.metadata.checkoutDate,
            context: service.metadata.context,
            hotelCode: service.metadata.hotelCode,
            boardCode: service.metadata.boardCode,
            boardDescription: service.metadata.boardDescription,
            paymentType: service.metadata.paymentType,
            refundable: service.metadata.refundable,
            currency: service.metadata.currency,
            finalPrice: service.metadata.finalPrice,
            markupId: service.markupRuleId,
            markupValue: service.markupValue,
            markupIsFixed: service.markupIsFixed,
            cancelPenalties: service.metadata.cancelPenalties || [],
            rooms: service.metadata.rooms || [],
            occupancies: service.metadata.occupancies || [],
            cancelPolicy: quotation?.cancelPolicy || service.metadata?.cancelPolicy || null,
            surcharges: quotation?.surcharges || [],
            priceMarkups: quotation?.price.markups || [],
            remarks: quotation?.remarks || null,
            gross: quotation?.price.gross,
            searchHistoryId: 0,
            exchangeRate: 0,
            net: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
            searchId: ' - ',
            status: ' - ',
            exchangeCurrency: ' - ',
            binding: false,
            supplements: [],
            rateRules:  service.metadata?.rateRules || [],
            minimumSellingPrice: null,
          }}
          onRemoveFromCart={
            canRemoveFromCart ? () => removeItem(service.id!) : undefined
          }
        />
      </Box >
    );
  }

  return null;
};
